import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { enumAttributeConverter } from '../enum-types';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlPaginationProps } from './pagination.props';
import { classMap } from 'lit/directives/class-map.js';

import * as TagNames from './pagination.tagnames';

import '../button';
import { AtlAriaButtonProps } from '../button';
import {
   AtlActivePageChangeEvent,
   AtlPageChangeEvent,
} from './pagination.events';
import { PaginationType } from './pagination.enums';

@customElement(TagNames.Pagination)
export class AtlPagination extends LitElement implements AtlPaginationProps {
   declare pages: number;
   declare firstRenderActivePage?: number;
   declare visiblePagesCap?: number;
   declare type: PaginationType;
   declare protected _activePage: number;

   static properties = {
      pages: { type: Number },
      firstRenderActivePage: { type: Number },
      visiblePagesCap: { type: Number },
      type: {
         type: String,
         converter: enumAttributeConverter(
            PaginationType,
            PaginationType.Numeric
         ),
      },
      _activePage: { type: Number, state: true },
   };

   constructor() {
      super();
      this.pages = 1;
      this._activePage = 1;

      this.addEventListener(AtlActivePageChangeEvent.EVENT_NAME, (event) => {
         this._activePage = (event as AtlActivePageChangeEvent).detail.payload;
      });
   }

   connectedCallback(): void {
      super.connectedCallback();
      if (
         this.firstRenderActivePage &&
         this._activePage !== this.firstRenderActivePage
      ) {
         if (
            this.firstRenderActivePage >= 1 &&
            this.firstRenderActivePage <= this.pages
         ) {
            this._activePage = this.firstRenderActivePage;
         }
      }
   }

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         atlantis-button[disabled]::part(button) {
            color: var(--tti-pagination-page-link-disabled-color) !important;
         }
      `,
   ];

   renderPageNumber(pageNum: number) {
      return this.type == PaginationType.Numeric
         ? html`<span>${pageNum}</span>`
         : html`<span class="fa-solid fa-minus medium"></span>`;
   }

   renderPrevButton() {
      const isDisabled = this._activePage <= 1;
      const classNames = { 'page-item': true, disabled: isDisabled };
      return html`
         <li class=${classMap(classNames)}>
            <atlantis-button
               variant="action"
               title="Previous Page"
               .buttonAriaSettings=${{
                  ariaLabel: 'Previous Page',
                  iconAriaHidden: true,
                  role: 'button',
               } as AtlAriaButtonProps}
               iconName="chevron-left"
               hiddenText="Previous Page"
               disabled="${isDisabled || nothing}"
               @click="${this._onPrevClick}"
            >
            </atlantis-button>
         </li>
      `;
   }

   renderPrevElipsis(visiblePageRange: number[]) {
      if (
         !this.pages ||
         !this.visiblePagesCap ||
         this.type === PaginationType.None
      )
         return nothing;

      if (
         !visiblePageRange ||
         visiblePageRange.length === 0 ||
         visiblePageRange.includes(1)
      )
         return nothing;

      return html`
         <li class="page-item">
            <button
               class="page-link btn btn-link"
               aria-label="Page 1"
               @click=${() => this._onPageClick(1)}
            >
               ${this.renderPageNumber(1)}
            </button>
         </li>
         <li class="page-item">&#46;&#46;&#46;</li>
      `;
   }

   renderVisiblePage(pageNum: number) {
      if (this.type === PaginationType.None) return nothing;

      const isDisabled = pageNum === this._activePage;
      const classNames = { 'page-item': true, disabled: isDisabled };

      return html` <li class=${classMap(classNames)}>
         <button
            class="page-link btn btn-link"
            ?disabled=${isDisabled === true}
            aria-label="Page ${pageNum}"
            role="button"
            @click="${() => this._onPageClick(pageNum)}"
         >
            ${this.renderPageNumber(pageNum)}
         </button>
      </li>`;
   }

   renderNextElipsis(visiblePageRange: number[]) {
      if (
         !this.pages ||
         !this.visiblePagesCap ||
         this.type === PaginationType.None
      )
         return nothing;

      if (
         !visiblePageRange ||
         visiblePageRange.length === 0 ||
         visiblePageRange.includes(this.pages)
      )
         return nothing;

      return html`
         <li class="page-item">&#46;&#46;&#46;</li>
         <li class="page-item">
            <button
               class="page-link btn btn-link"
               aria-label="Page ${this.pages}"
               @click=${() => this._onPageClick(this.pages)}
            >
               ${this.renderPageNumber(this.pages)}
            </button>
         </li>
      `;
   }

   renderNextButton() {
      const isDisabled = !this.pages || this._activePage >= this.pages;
      return html`
         <li class=${classMap({ 'page-item': true, disabled: isDisabled })}>
            <atlantis-button
               variant="action"
               title="Next Page"
               .buttonAriaSettings=${{
                  ariaLabel: 'Next Page',
                  iconAriaHidden: true,
                  role: 'button',
               } as AtlAriaButtonProps}
               iconName="chevron-right"
               hiddenText="Next Page"
               ?disabled=${isDisabled === true}
               @click="${this._onNextClick}"
            >
            </atlantis-button>
         </li>
      `;
   }

   render() {
      const visiblePageRange = this._calculateVisiblePageRange();
      return html`
         <ul class="pagination" part="pagination">
            ${this.renderPrevButton()}
            ${this.renderPrevElipsis(visiblePageRange)}
            ${html`${visiblePageRange.map((pageNum) =>
               this.renderVisiblePage(pageNum)
            )}`}
            ${this.renderNextElipsis(visiblePageRange)}
            ${this.renderNextButton()}
         </ul>
      `;
   }

   protected _onPrevClick() {
      if (this._activePage > 1) {
         this._setPageAndDispatch(this._activePage - 1);
      }
   }

   protected _onNextClick() {
      if (this._activePage < this.pages) {
         this._setPageAndDispatch(this._activePage + 1);
      }
   }

   protected _onPageClick(pageNum: number) {
      if (
         pageNum >= 1 &&
         pageNum <= this.pages &&
         pageNum !== this._activePage
      ) {
         this._setPageAndDispatch(pageNum);
      }
   }

   protected _setPageAndDispatch(newPage: number) {
      this._activePage = newPage;
      this.dispatchEvent(new AtlPageChangeEvent({ payload: newPage }));
   }

   private _calculateVisiblePageRange() {
      let startRange = 1;
      let endRange = this.pages;

      if (this.pages <= 1) return [1];

      if (this.visiblePagesCap && this.pages > this.visiblePagesCap) {
         if (this._activePage <= this.visiblePagesCap) {
            startRange = 1;
            endRange = this.visiblePagesCap;
         } else if (this._activePage > this.pages - this.visiblePagesCap) {
            startRange = this.pages - this.visiblePagesCap + 1;
            endRange = startRange + this.visiblePagesCap - 1;
         } else {
            startRange = Math.max(
               0,
               this._activePage - Math.floor(this.visiblePagesCap / 2)
            );
            endRange = startRange + this.visiblePagesCap - 1;
         }
      }

      return Array.from(
         { length: endRange - startRange + 1 },
         (_, i) => i + startRange
      );
   }
}
